import { Pencil } from "lucide-react";
import { PersonaAvatar } from "@/components/persona-identity";
import { VoiceProse } from "@/components/ui/voice-prose";
import { PaperCard } from "@/components/ui/paper-card";
import { Kicker } from "@/components/ui/kicker";
import { CollapsibleSection } from "@/components/collapsible-section";
import { useCanEdit, useEditorCopy } from "@/lib/project-access";

interface PersonaIdentitySectionProps {
  persona: {
    id: string;
    name: string;
    role: string;
    brief?: string | null;
    context: string | null;
    disposition: string | null;
  };
  onEdit: () => void;
}

function previewOf(text: string, words: number): string {
  const parts = text.trim().split(/\s+/);
  const preview = parts.slice(0, words).join(" ");
  return parts.length > words ? `${preview}…` : preview;
}

function InlineEditButton({
  label,
  onClick,
}: {
  label: string;
  onClick: () => void;
}) {
  const canEdit = useCanEdit();
  if (!canEdit) return null;
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      className="inline-flex items-center gap-1 text-sm text-muted-foreground underline-offset-4 transition-colors hover:text-foreground hover:underline focus-visible:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background rounded"
    >
      <Pencil className="h-3 w-3" aria-hidden="true" />
      Edit
    </button>
  );
}

function NoBackgroundCopy({ firstName }: { firstName: string }) {
  return useEditorCopy(
    `No background yet — add a few sentences about ${firstName}'s role, experience, and working reality.`,
    "No background yet.",
  );
}

export function PersonaIdentitySection({
  persona,
  onEdit,
}: PersonaIdentitySectionProps) {
  const firstName = persona.name.split(" ")[0];

  return (
    <div className="space-y-6">
      {/* ── Identity + background hero ──────────────────────────── */}
      <PaperCard as="section" padding="lg" className="space-y-8">
        <div className="flex items-start gap-5">
          <PersonaAvatar name={persona.name} size="lg" />
          <div className="min-w-0 space-y-1 pt-1">
            <div className="text-md font-medium">{persona.role}</div>
            {persona.brief?.trim() && (
              <p className="text-sm text-foreground pt-1">{persona.brief}</p>
            )}
            <Kicker>
              A durable lens — {firstName}'s perspective across this project
            </Kicker>
          </div>
        </div>

        <div className="space-y-4">
          <div className="flex items-baseline justify-between gap-3">
            <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
              <Kicker as="h2">Background</Kicker>
              <span className="text-sm text-muted-foreground">
                Who {firstName} is on any given day
              </span>
            </div>
            <InlineEditButton label="Edit background" onClick={onEdit} />
          </div>
          {persona.context ? (
            <VoiceProse className="max-w-[70ch]">{persona.context}</VoiceProse>
          ) : (
            <p className="text-sm text-muted-foreground">
              <NoBackgroundCopy firstName={firstName} />
            </p>
          )}
        </div>
      </PaperCard>

      {/* ── Disposition ─────────────────────────────────────────── */}
      {persona.disposition && (
        <CollapsibleSection
          label="Disposition"
          storageKey={`persona-detail-${persona.id}-disposition`}
          previewText={previewOf(persona.disposition, 16)}
          ariaLabel={`${firstName}'s disposition`}
        >
          <div className="space-y-3">
            <VoiceProse className="max-w-[70ch]">
              {persona.disposition}
            </VoiceProse>
            <div>
              <InlineEditButton label="Edit disposition" onClick={onEdit} />
            </div>
          </div>
        </CollapsibleSection>
      )}
    </div>
  );
}
